<template>
  <div class="operations-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER ==================== -->
    <div class="op-header-row mb-6">
      <div class="op-header-titles">
        <div class="d-flex align-center gap-2 mb-2 flex-wrap">
          <span class="op-badge-section">
            <v-icon x-small color="#082C69" class="me-1">mdi-view-dashboard-outline</v-icon>
            مركز التشغيل اليومي
          </span>
        </div>

        <h1 class="op-main-title">لوحة التحكم والعمليات الميدانية</h1>
        <p class="op-subtitle">نظرة عامة على الرحلات والمركبات والسائقين ضمن مجموعات التشغيل المسندة إليك</p>
      </div>

      <!-- Header Actions -->
      <div class="op-header-actions d-flex align-center gap-2">
        <div class="op-date-box d-flex align-center">
          <v-icon small color="#082C69" class="me-2">mdi-calendar-today</v-icon>
          <span class="op-date-text">{{ formattedTodayDate }}</span>
        </div>

        <v-btn
          depressed
          class="op-btn-refresh"
          :loading="isLoading"
          :disabled="isLoading"
          @click="fetchDashboardData"
        >
          <v-icon size="18" color="#082C69">mdi-refresh</v-icon>
          <span class="ms-1 font-weight-bold">تحديث</span>
        </v-btn>
      </div>
    </div>

    <!-- ==================== NO ASSIGNED GROUPS EMPTY STATE ==================== -->
    <div v-if="!isLoading && !dashboard.scope.has_assignments" class="op-unassigned-card text-center py-12 px-6">
      <div class="op-empty-icon-circle mx-auto mb-4">
        <v-icon size="48" color="#64748B">mdi-folder-alert-outline</v-icon>
      </div>
      <h2 class="op-empty-title mb-2">لا توجد مجموعات رحلات مسندة إليك حالياً</h2>
      <p class="op-empty-desc mx-auto">
        حسابك التشغيلي نشط، ولكن لم يتم إسناد أي مجموعة رحلات (Trip Group) إليك بعد. يرجى التواصل مع إدارة النظام لتعيين نطاقك التشغيلي لبدء متابعة الرحلات والمركبات والسائقين.
      </p>
      <div class="d-inline-flex align-center gap-2 op-empty-badge mt-4">
        <v-icon small color="#082C69">mdi-shield-account-outline</v-icon>
        <span>حساب مسؤول عمليات: <strong>{{ userEmail }}</strong></span>
      </div>
    </div>

    <!-- ==================== DASHBOARD CONTENT (WHEN SCOPED) ==================== -->
    <div v-else>
      <!-- ==================== 1. TOP KPI CARDS ==================== -->
      <v-row class="mb-4">
        <!-- KPI 1: Today's Trips -->
        <v-col cols="12" sm="6" md="3" class="py-2">
          <div class="op-kpi-card">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="op-kpi-label">إجمالي الرحلات اليوم</span>
              <div class="op-kpi-icon-box box-blue">
                <v-icon size="22" color="#082C69">mdi-bus-clock</v-icon>
              </div>
            </div>
            <div class="op-kpi-val">{{ dashboard.summary.today_trips }}</div>
            <div class="op-kpi-subtext text-muted">
              رحلة مجدولة لتشغيل اليوم
            </div>
          </div>
        </v-col>

        <!-- KPI 2: Vehicles Without Driver -->
        <v-col cols="12" sm="6" md="3" class="py-2">
          <div class="op-kpi-card">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="op-kpi-label">مركبات بدون سائق</span>
              <div class="op-kpi-icon-box box-amber">
                <v-icon size="22" color="#D97706">mdi-account-alert-outline</v-icon>
              </div>
            </div>
            <div class="op-kpi-val" :class="{ 'text-amber': dashboard.summary.vehicles_without_driver > 0 }">
              {{ dashboard.summary.vehicles_without_driver }}
            </div>
            <div class="op-kpi-subtext">
              <span v-if="dashboard.summary.vehicles_without_driver > 0" class="text-amber font-weight-bold">
                تتطلب تعيين سائق فوراً
              </span>
              <span v-else class="text-success font-weight-medium">
                جميع المركبات معينة
              </span>
            </div>
          </div>
        </v-col>

        <!-- KPI 3: Working Vehicles -->
        <v-col cols="12" sm="6" md="3" class="py-2">
          <div class="op-kpi-card">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="op-kpi-label">المركبات العاملة</span>
              <div class="op-kpi-icon-box box-green">
                <v-icon size="22" color="#059669">mdi-bus</v-icon>
              </div>
            </div>
            <div class="op-kpi-val text-green">{{ dashboard.summary.working_vehicles }}</div>
            <div class="op-kpi-subtext text-muted">
              مركبة مدرجة في جدول اليوم
            </div>
          </div>
        </v-col>

        <!-- KPI 4: Online Drivers -->
        <v-col cols="12" sm="6" md="3" class="py-2">
          <div class="op-kpi-card">
            <div class="d-flex align-center justify-space-between mb-3">
              <span class="op-kpi-label">السائقون أونلاين</span>
              <div class="op-kpi-icon-box box-sky">
                <v-icon size="22" color="#0284C7">mdi-account-check-outline</v-icon>
              </div>
            </div>
            <div class="op-kpi-val text-sky">{{ dashboard.summary.online_drivers }}</div>
            <div class="op-kpi-subtext text-muted">
              سائق نشط بموقع حي / رحلة جارية
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- ==================== 2. MIDDLE ROW: STATUS CARDS & LIVE MAP ==================== -->
      <v-row class="mb-4">
        <!-- 2.1 Trip Status Summary -->
        <v-col cols="12" sm="6" md="6" lg="3" class="py-2">
          <div class="op-card">
            <div class="op-card-header d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">mdi-chart-donut</v-icon>
                <h2 class="op-card-title">حالة الرحلات اليوم</h2>
              </div>
              <span class="op-pill-count">{{ dashboard.trip_status.total }}</span>
            </div>

            <div class="op-status-list">
              <div class="op-status-item d-flex align-center justify-space-between py-2 border-bottom">
                <div class="d-flex align-center">
                  <span class="status-dot dot-running me-2"></span>
                  <span class="caption font-weight-medium">جارية الآن</span>
                </div>
                <span class="caption font-weight-bold text-green">{{ dashboard.trip_status.running }}</span>
              </div>

              <div class="op-status-item d-flex align-center justify-space-between py-2 border-bottom">
                <div class="d-flex align-center">
                  <span class="status-dot dot-ontime me-2"></span>
                  <span class="caption font-weight-medium">في الموعد / قادمة</span>
                </div>
                <span class="caption font-weight-bold text-blue">{{ dashboard.trip_status.on_time }}</span>
              </div>

              <div class="op-status-item d-flex align-center justify-space-between py-2 border-bottom">
                <div class="d-flex align-center">
                  <span class="status-dot dot-delayed me-2"></span>
                  <span class="caption font-weight-medium">متأخرة</span>
                </div>
                <span class="caption font-weight-bold text-amber">{{ dashboard.trip_status.delayed }}</span>
              </div>

              <div class="op-status-item d-flex align-center justify-space-between py-2 border-bottom">
                <div class="d-flex align-center">
                  <span class="status-dot dot-completed me-2"></span>
                  <span class="caption font-weight-medium">مكتملة</span>
                </div>
                <span class="caption font-weight-bold text-teal">{{ dashboard.trip_status.completed }}</span>
              </div>

              <div class="op-status-item d-flex align-center justify-space-between py-2 border-bottom">
                <div class="d-flex align-center">
                  <span class="status-dot dot-cancelled me-2"></span>
                  <span class="caption font-weight-medium">ملغاة</span>
                </div>
                <span class="caption font-weight-bold text-red">{{ dashboard.trip_status.cancelled }}</span>
              </div>

              <div class="op-status-item d-flex align-center justify-space-between py-2">
                <div class="d-flex align-center">
                  <span class="status-dot dot-paused me-2"></span>
                  <span class="caption font-weight-medium">متوقفة مؤقتاً</span>
                </div>
                <span class="caption font-weight-bold text-purple">{{ dashboard.trip_status.paused }}</span>
              </div>
            </div>
          </div>
        </v-col>

        <!-- 2.2 Driver Status Summary -->
        <v-col cols="12" sm="6" md="6" lg="3" class="py-2">
          <div class="op-card">
            <div class="op-card-header d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">mdi-account-tie-hat</v-icon>
                <h2 class="op-card-title">حالة السائقين</h2>
              </div>
              <span class="op-pill-count">{{ dashboard.driver_status.total }}</span>
            </div>

            <div class="op-status-list">
              <div class="op-status-item d-flex align-center justify-space-between py-3 border-bottom">
                <div class="d-flex align-center">
                  <span class="status-dot dot-running me-2"></span>
                  <span class="caption font-weight-medium">أونلاين (نشط ميدانياً)</span>
                </div>
                <span class="caption font-weight-bold text-green">{{ dashboard.driver_status.online }}</span>
              </div>

              <div class="op-status-item d-flex align-center justify-space-between py-3 border-bottom">
                <div class="d-flex align-center">
                  <span class="status-dot dot-offline me-2"></span>
                  <span class="caption font-weight-medium">غير متصل حالياً</span>
                </div>
                <span class="caption font-weight-bold text-muted">{{ dashboard.driver_status.offline }}</span>
              </div>

              <div class="op-status-item d-flex align-center justify-space-between py-3">
                <div class="d-flex align-center">
                  <span class="status-dot dot-cancelled me-2"></span>
                  <span class="caption font-weight-medium">موقوف / غير نشط</span>
                </div>
                <span class="caption font-weight-bold text-red">{{ dashboard.driver_status.inactive }}</span>
              </div>
            </div>

            <div class="op-hint-box mt-4 p-2">
              <v-icon x-small color="#64748B" class="me-1">mdi-information-outline</v-icon>
              <span class="caption text-muted">يشمل السائقين المسندين لرحلات نطاقك اليومي.</span>
            </div>
          </div>
        </v-col>

        <!-- 2.3 Vehicle Status Summary -->
        <v-col cols="12" sm="6" md="6" lg="3" class="py-2">
          <div class="op-card">
            <div class="op-card-header d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">mdi-bus-multiple</v-icon>
                <h2 class="op-card-title">حالة المركبات</h2>
              </div>
              <span class="op-pill-count">{{ dashboard.vehicle_status.total }}</span>
            </div>

            <div class="op-status-list">
              <div class="op-status-item d-flex align-center justify-space-between py-3 border-bottom">
                <div class="d-flex align-center">
                  <span class="status-dot dot-running me-2"></span>
                  <span class="caption font-weight-medium">عاملة في جدول اليوم</span>
                </div>
                <span class="caption font-weight-bold text-green">{{ dashboard.vehicle_status.working }}</span>
              </div>

              <div class="op-status-item d-flex align-center justify-space-between py-3 border-bottom">
                <div class="d-flex align-center">
                  <span class="status-dot dot-delayed me-2"></span>
                  <span class="caption font-weight-medium">بدون سائق</span>
                </div>
                <span class="caption font-weight-bold text-amber">{{ dashboard.vehicle_status.without_driver }}</span>
              </div>

              <div class="op-status-item d-flex align-center justify-space-between py-3">
                <div class="d-flex align-center">
                  <span class="status-dot dot-offline me-2"></span>
                  <span class="caption font-weight-medium">غير عاملة / معطلة</span>
                </div>
                <span class="caption font-weight-bold text-muted">{{ dashboard.vehicle_status.idle }}</span>
              </div>
            </div>

            <div class="op-hint-box mt-4 p-2">
              <v-icon x-small color="#64748B" class="me-1">mdi-information-outline</v-icon>
              <span class="caption text-muted">حصر أسطول المركبات المرتبط برحلات مجموعاتك.</span>
            </div>
          </div>
        </v-col>

        <!-- 2.4 Live Tracking Mini Map -->
        <v-col cols="12" sm="6" md="6" lg="3" class="py-2">
          <div class="op-card d-flex flex-column">
            <div class="op-card-header d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <v-icon size="20" color="#082C69" class="me-2">mdi-crosshairs-gps</v-icon>
                <h2 class="op-card-title">التتبع المباشر</h2>
              </div>
              <span class="op-badge-pill-live" :class="{ 'live-active': dashboard.live_map.length > 0 }">
                <span class="live-dot me-1"></span>
                {{ dashboard.live_map.length }} مركبة
              </span>
            </div>

            <div class="op-map-container flex-grow-1 position-relative">
              <google-map-loader
                v-if="dashboard.live_map.length > 0"
                :center="mapCenter"
                :zoom="11"
                :markers="mapMarkers"
                style="height: 220px; width: 100%; border-radius: 12px; overflow: hidden;"
              />
              <div v-else class="op-map-empty d-flex flex-column align-center justify-center text-center py-8">
                <v-icon size="36" color="#94A3B8" class="mb-2">mdi-map-marker-off-outline</v-icon>
                <div class="caption font-weight-bold text-muted">لا توجد مركبات متحركة حالياً</div>
                <div class="caption text-muted mt-1">ستظهر مواقع المركبات تلقائياً فور انطلاق الرحلات</div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- ==================== 3. BOTTOM ROW: TABLES & ALERTS ==================== -->
      <v-row>
        <!-- 3.1 Running Trips Table -->
        <v-col cols="12" md="6" lg="5" class="py-2">
          <div class="op-card">
            <div class="op-card-header d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon size="20" color="#059669" class="me-2">mdi-motion-play-outline</v-icon>
                <h2 class="op-card-title">الرحلات الجارية الآن</h2>
              </div>
              <span class="op-pill-count bg-green-light text-green">{{ dashboard.running_trips.length }}</span>
            </div>

            <div v-if="dashboard.running_trips.length > 0" class="table-responsive">
              <table class="op-table w-100">
                <thead>
                  <tr>
                    <th>الموعد</th>
                    <th>الرحلة</th>
                    <th>المسار</th>
                    <th>المركبة</th>
                    <th>السائق</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="rt in dashboard.running_trips" :key="rt.id">
                    <td class="caption font-weight-bold">{{ formatTime(rt.start_time) }}</td>
                    <td>
                      <span class="op-code-pill font-weight-bold">{{ rt.planned_trip_reference }}</span>
                    </td>
                    <td>
                      <div class="d-flex flex-column">
                        <span class="caption font-weight-bold text-navy text-truncate" style="max-width: 130px;">{{ rt.route_name }}</span>
                        <span class="caption text-muted">{{ rt.route_code }}</span>
                      </div>
                    </td>
                    <td class="caption">{{ rt.vehicle }}</td>
                    <td class="caption font-weight-medium">{{ rt.driver }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="text-center py-8 text-muted caption">
              <v-icon small color="#94A3B8" class="mb-1 d-block mx-auto">mdi-bus-stop</v-icon>
              لا توجد رحلات جارية على الطريق في الوقت الحالي
            </div>
          </div>
        </v-col>

        <!-- 3.2 Delayed Trips Table -->
        <v-col cols="12" md="6" lg="4" class="py-2">
          <div class="op-card">
            <div class="op-card-header d-flex align-center justify-space-between mb-4">
              <div class="d-flex align-center">
                <v-icon size="20" color="#D97706" class="me-2">mdi-clock-alert-outline</v-icon>
                <h2 class="op-card-title">الرحلات المتأخرة</h2>
              </div>
              <span class="op-pill-count bg-amber-light text-amber">{{ dashboard.delayed_trips.length }}</span>
            </div>

            <div v-if="dashboard.delayed_trips.length > 0" class="table-responsive">
              <table class="op-table w-100">
                <thead>
                  <tr>
                    <th>مقدار التأخير</th>
                    <th>الرحلة</th>
                    <th>المسار</th>
                    <th>الموعد</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="dt in dashboard.delayed_trips" :key="dt.id">
                    <td>
                      <span class="op-delay-badge">{{ dt.delay_label }}</span>
                    </td>
                    <td>
                      <span class="op-code-pill">{{ dt.planned_trip_reference }}</span>
                    </td>
                    <td>
                      <span class="caption font-weight-bold text-navy text-truncate d-block" style="max-width: 110px;">
                        {{ dt.route_name }}
                      </span>
                    </td>
                    <td class="caption text-muted">{{ formatTime(dt.scheduled_time) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else class="text-center py-8 text-muted caption">
              <v-icon small color="#059669" class="mb-1 d-block mx-auto">mdi-check-circle-outline</v-icon>
              جميع الرحلات تسير وفق الجدول المحدد دون تأخير
            </div>
          </div>
        </v-col>

        <!-- 3.3 Current Alerts -->
        <v-col cols="12" md="12" lg="3" class="py-2">
          <div class="op-card op-alerts-card">
            <div class="op-card-header d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center">
                <v-icon size="20" color="#EF4444" class="me-2">mdi-bell-alert-outline</v-icon>
                <h2 class="op-card-title">التنبيهات الحالية</h2>
              </div>
              <span class="op-pill-count" :class="dashboard.alerts.length > 0 ? 'bg-red-light text-red' : ''">
                {{ dashboard.alerts.length }}
              </span>
            </div>

            <div v-if="dashboard.alerts.length > 0" class="op-alerts-list">
              <div
                v-for="alert in dashboard.alerts"
                :key="alert.id"
                class="op-alert-item d-flex align-start mb-3 p-2 rounded"
                :class="'alert-' + alert.type"
              >
                <v-icon size="18" class="me-2 flex-shrink-0 mt-1" :class="'icon-' + alert.type">
                  {{ alert.icon }}
                </v-icon>
                <div class="flex-grow-1 op-alert-body">
                  <div class="caption font-weight-bold">{{ alert.title }}</div>
                  <div class="caption text-muted">{{ alert.message }}</div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-8 text-muted caption">
              <v-icon small color="#059669" class="mb-1 d-block mx-auto">mdi-bell-check-outline</v-icon>
              لا توجد تنبيهات تشغيلية حالياً
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VueElementLoading from "vue-element-loading";
import GoogleMapLoader from "@/components/GoogleMapLoader.vue";

export default {
  name: "OperationsHome",
  components: {
    VueElementLoading,
    GoogleMapLoader,
  },
  data() {
    return {
      isLoading: false,
      refreshInterval: null,
      dashboard: {
        scope: {
          has_assignments: false,
          groups_count: 0,
          groups: [],
          parent_trips_count: 0,
        },
        summary: {
          today_trips: 0,
          working_vehicles: 0,
          vehicles_without_driver: 0,
          online_drivers: 0,
        },
        trip_status: {
          running: 0,
          on_time: 0,
          delayed: 0,
          cancelled: 0,
          completed: 0,
          paused: 0,
          total: 0,
        },
        driver_status: {
          online: 0,
          offline: 0,
          inactive: 0,
          total: 0,
        },
        vehicle_status: {
          working: 0,
          without_driver: 0,
          idle: 0,
          total: 0,
        },
        running_trips: [],
        delayed_trips: [],
        alerts: [],
        live_map: [],
        server_time: null,
        today_date: null,
      },
    };
  },
  computed: {
    userEmail() {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      return u.email || "operation@beepgo.net";
    },
    formattedTodayDate() {
      try {
        const d = new Date();
        const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
        return d.toLocaleDateString("ar-EG", options);
      } catch (e) {
        return new Date().toISOString().substring(0, 10);
      }
    },
    mapCenter() {
      if (this.dashboard.live_map.length > 0) {
        return this.dashboard.live_map[0].position;
      }
      // Default Cairo coordinates
      return { lat: 30.0444, lng: 31.2357 };
    },
    mapMarkers() {
      return this.dashboard.live_map.map((m) => ({
        position: m.position,
        title: m.title,
        icon: {
          path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
          fillColor: "#059669",
          fillOpacity: 1,
          scale: 1.4,
          strokeColor: "#FFFFFF",
          strokeWeight: 2,
        },
      }));
    },
  },
  created() {
    this.fetchDashboardData();
  },
  mounted() {
    // Refresh operational metrics every 30 seconds
    this.refreshInterval = setInterval(() => {
      this.fetchDashboardData(true);
    }, 30000);
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  methods: {
    fetchDashboardData(silent = false) {
      if (!silent) {
        this.isLoading = true;
      }
      axios
        .get("/operations/dashboard")
        .then((response) => {
          if (response.data) {
            this.dashboard = {
              ...this.dashboard,
              ...response.data,
            };
          }
        })
        .catch((error) => {
          console.error("Error fetching operations dashboard:", error);
          if (!silent) {
            this.notifyUser("error", "خطأ في الاتصال", "تعذر تحميل بيانات مركز التشغيل اليومي");
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatTime(timeStr) {
      if (!timeStr) return "—";
      const parts = timeStr.split(":");
      if (parts.length < 2) return timeStr;
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "م" : "ص";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    },
    notifyUser(type, title, text) {
      if (this.$notify) {
        this.$notify({
          title: title || (type === "success" ? "تم بنجاح" : "خطأ"),
          text: text,
          type: type,
        });
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap");

.operations-container {
  font-family: "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  color: #0F172A;
  padding: 16px 20px 40px;
}

/* Header */
.op-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.op-badge-section {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: rgba(8, 44, 105, 0.08);
  color: #082C69;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
}

.op-scope-pill {
  padding: 4px 12px;
  background: #E0F2FE;
  border: 1px solid #BAE6FD;
  border-radius: 20px;
  color: #0369A1;
  font-size: 12px;
}

.op-no-scope-pill {
  padding: 4px 12px;
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  border-radius: 20px;
  color: #64748B;
  font-size: 12px;
}

.op-main-title {
  font-size: 26px;
  font-weight: 800;
  color: #082C69;
  margin: 4px 0;
}

.op-subtitle {
  font-size: 14px;
  color: #64748B;
  margin: 0;
}

.op-date-box {
  padding: 8px 16px;
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #082C69;
}

.op-btn-refresh {
  background: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 12px !important;
  height: 40px !important;
  text-transform: none !important;
}

/* Cards */
.op-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px -2px rgba(8, 44, 105, 0.04);
}

.op-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #082C69;
  margin: 0;
}

.op-pill-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 24px;
  padding: 0 8px;
  border-radius: 12px;
  background: #F1F5F9;
  font-size: 12px;
  font-weight: 700;
  color: #475569;
}

/* KPI Cards */
.op-kpi-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px -2px rgba(8, 44, 105, 0.04);
  transition: transform 0.2s, box-shadow 0.2s;
}

.op-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px -2px rgba(8, 44, 105, 0.08);
}

.op-kpi-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748B;
}

.op-kpi-val {
  font-size: 32px;
  font-weight: 800;
  color: #082C69;
  line-height: 1.1;
  margin-bottom: 6px;
}

.op-kpi-subtext {
  font-size: 12px;
}

.op-kpi-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.box-blue { background: rgba(8, 44, 105, 0.08); }
.box-amber { background: rgba(245, 158, 11, 0.12); }
.box-green { background: rgba(16, 185, 129, 0.12); }
.box-sky { background: rgba(2, 132, 199, 0.12); }

/* Status Dots */
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.dot-running { background: #10B981; }
.dot-ontime { background: #0284C7; }
.dot-delayed { background: #F59E0B; }
.dot-completed { background: #0D9488; }
.dot-cancelled { background: #EF4444; }
.dot-paused { background: #8B5CF6; }
.dot-offline { background: #94A3B8; }

/* Colors */
.text-green { color: #059669 !important; }
.text-blue { color: #0284C7 !important; }
.text-amber { color: #D97706 !important; }
.text-teal { color: #0D9488 !important; }
.text-red { color: #DC2626 !important; }
.text-purple { color: #7C3AED !important; }
.text-sky { color: #0284C7 !important; }

.bg-green-light { background: #D1FAE5 !important; }
.bg-amber-light { background: #FEF3C7 !important; }
.bg-red-light { background: #FEE2E2 !important; }

/* Live Map Badge */
.op-badge-pill-live {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  background: #F1F5F9;
  color: #64748B;
  font-size: 11px;
  font-weight: 700;
}

.op-badge-pill-live.live-active {
  background: #ECFDF5;
  color: #059669;
}

.live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #10B981;
  display: inline-block;
}

.op-map-container {
  min-height: 220px;
  background: #F8FAFC;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #E2E8F0;
}

/* Tables */
.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.gap-2 {
  gap: 8px;
}

.op-table {
  width: 100%;
  border-collapse: collapse;
}

.op-table th {
  padding: 8px 10px;
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  text-align: right;
  border-bottom: 1px solid #E2E8F0;
}

.op-table td {
  padding: 10px 10px;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}

.op-table tr:last-child td {
  border-bottom: none;
}

.op-code-pill {
  display: inline-block;
  padding: 2px 8px;
  background: #F1F5F9;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #082C69;
}

.op-delay-badge {
  display: inline-block;
  padding: 3px 8px;
  background: #FEF3C7;
  color: #B45309;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
}

/* Alerts */
.op-alerts-card {
  display: flex;
  flex-direction: column;
  max-height: 380px;
  overflow: hidden;
}

.op-alerts-card .op-card-header {
  flex-shrink: 0;
}

.op-alerts-list {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-left: 4px;
  scrollbar-width: thin;
  scrollbar-color: #CBD5E1 transparent;
}

.op-alerts-list::-webkit-scrollbar {
  width: 5px;
}

.op-alerts-list::-webkit-scrollbar-track {
  background: transparent;
}

.op-alerts-list::-webkit-scrollbar-thumb {
  background-color: #CBD5E1;
  border-radius: 6px;
}

.op-alerts-list .op-alert-item:last-child {
  margin-bottom: 0 !important;
}

.op-alert-body {
  min-width: 0;
  word-break: break-word;
}

@media (max-width: 960px) {
  .op-alerts-card {
    max-height: 320px;
  }
  .op-alerts-list {
    max-height: 240px;
  }
}

.op-alert-item {
  border: 1px solid transparent;
}

.alert-warning {
  background: #FFFBEB;
  border-color: #FDE68A;
}
.icon-warning { color: #D97706; }

.alert-error {
  background: #FEF2F2;
  border-color: #FECACA;
}
.icon-error { color: #DC2626; }

.alert-info {
  background: #F0FDF4;
  border-color: #BBF7D0;
}
.icon-info { color: #16A34A; }

/* Empty state */
.op-unassigned-card {
  background: #FFFFFF;
  border: 1px dashed #CBD5E1;
  border-radius: 24px;
}

.op-empty-icon-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.op-empty-title {
  font-size: 20px;
  font-weight: 700;
  color: #1E293B;
}

.op-empty-desc {
  max-width: 500px;
  font-size: 14px;
  color: #64748B;
  line-height: 1.6;
}

.op-empty-badge {
  padding: 6px 16px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 20px;
  font-size: 13px;
  color: #475569;
}
</style>
